import { LocalizationProvider } from '@mui/x-date-pickers';
import * as Sentry from '@sentry/react';
import { ErrorFallback } from 'components/common/ErrorFallback/ErrorFallback.js';
import { SandboxBanner } from 'components/layout/SandboxBanner/index.js';
import { DateAdapter } from 'components/ui/form/DatePicker/DatePicker.js';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import {
  StrictMode,
  Suspense,
  useEffect,
} from 'react';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { getMultiModalConfig } from 'shared/utils/multiModalConfig.js';

import { App } from './app/app.js';
import {
  EnvName,
  envName,
} from './env.js';
import { ThemeProvider } from './theme/ThemeProvider.js';

const sentryConfig = {
  // dsn has to be in sync with backend's frontendDsn (in config.facade.ts at this moment)
  dsn: 'https://11cb197c4f1e4906d26f8a4720d4d27a@o4511537668358144.ingest.de.sentry.io/4511537714692176',
  // ideally backend would rewrite this in the tunnel /api/bugs but it's not easy
  env: (() => {
    switch (envName) { // backend should follow the same logic
      case EnvName.LOCAL: return 'development';
      case EnvName.TEST: return 'development';
      case EnvName.DEV: return 'development';
      case EnvName.STAGING: return 'staging';
      case EnvName.PRODUCTION: return 'production';
      default: {
        const _: never = envName;
        throw new Error(`Unknown env name: ${envName}`);
      }
    }
  })(),
};

const baseApiUrl = getMultiModalConfig()?.baseApiUrl;

Sentry.init({
  dsn: sentryConfig.dsn,
  tunnel: baseApiUrl ? new URL('/api/bugs', baseApiUrl).toString() : '/api/bugs',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin],
      maskAllInputs: false,
      maskAllText: false,
    }),
  ],
  environment: sentryConfig.env,
  enabled: true, // frontend is always enabled, because it tunnels through backend that checks if enabled
  replaysOnErrorSampleRate: 1,
  replaysSessionSampleRate: 0,
  tracesSampleRate: 0.5,
  maxValueLength: 8192,
});

export const Main = ({ basename }: { basename?: string }) => (
  <StrictMode>
    <ThemeProvider>
      <SandboxBanner />
      <Sentry.ErrorBoundary fallback={ErrorFallback}>
        <BrowserRouter basename={basename}>
          <NuqsAdapter>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <Suspense>
                <App />
              </Suspense>
            </LocalizationProvider>
          </NuqsAdapter>
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  </StrictMode>
);

export default Main;
